import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { usePermissions } from '../../utils/permissions';
import {
  Box, Container, Typography, Button, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Alert, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Fab, Card, CardContent, Snackbar, useTheme,
  alpha, Fade, LinearProgress, InputAdornment, Grid, Divider, Tooltip, Chip,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon,
  Close as CloseIcon, Search as SearchIcon, Build as BuildIcon,
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  ArrowBack as ArrowBackIcon, CloudUpload as CloudUploadIcon,
  PictureAsPdf as PictureAsPdfIcon, Visibility as VisibilityIcon,
  Download as DownloadIcon, CheckCircle as CheckCircleIcon,
  Check as CheckIcon, Cancel as CancelIcon, Verified as VerifiedIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

const EquipmentMaintenance = () => {
  const { companyId, departmentId, equipmentId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { hasPermission } = usePermissions();
  
  // State management
  const [equipment, setEquipment] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [equipmentDetails, setEquipmentDetails] = useState(null);
  const [maintenanceLogs, setMaintenanceLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [logToDelete, setLogToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [expandedRows, setExpandedRows] = useState(new Set()); // Track which log rows are expanded
  
  // Approval dialog states
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalLog, setApprovalLog] = useState(null);
  const [signatureName, setSignatureName] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  
  // Selection dialogs
  const [departmentDialogOpen, setDepartmentDialogOpen] = useState(false);
  const [equipmentDialogOpen, setEquipmentDialogOpen] = useState(false);
  const [departmentSearchTerm, setDepartmentSearchTerm] = useState('');
  const [equipmentSearchTerm, setEquipmentSearchTerm] = useState('');
  const [dialogSelectedDepartment, setDialogSelectedDepartment] = useState(null);
  const [dialogSelectedEquipment, setDialogSelectedEquipment] = useState(null);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [filteredEquipment, setFilteredEquipment] = useState([]);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Time format preference state [[memory:8555083]]
  const [timeFormat, setTimeFormat] = useState('24hour');
  const [userProfile, setUserProfile] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    id: '',
    serial_number: '',
    log_date: dayjs(),
    breakdown_details: '',
    nature_of_breakdown: '',
    stopped_at: null,
    restarted_at: null,
    spare_parts: []
  });
  
  const [detailsFormData, setDetailsFormData] = useState({
    power_rating: '',
    calibration_requirement: '',
    frequency_of_calibration: '',
    date_of_installation: null,
    warranty_period: '',
    amc_info: ''
  });

  const fetchCompanyData = async () => {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single();
      if (error) throw error;
      setCompanyName(data?.name || 'Company');
    } catch (err) {
      console.error('Error fetching company:', err);
      setCompanyName('Company Name');
    }
  };

  const fetchDepartments = async () => {
    try {
      const { data, error } = await supabase
        .from('departments')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name', { ascending: true });
      if (error) throw error;
      setDepartments(data || []);
    } catch (err) {
      console.error('Error fetching departments:', err);
      setError('Failed to load departments.');
    }
  };

  const fetchEquipment = async () => {
    if (!selectedDepartment) return;
    try {
      const { data, error } = await supabase
        .from('equipment')
        .select('*')
        .eq('company_id', companyId)
        .eq('department_id', selectedDepartment.id)
        .order('equipment_name', { ascending: true });
      if (error) throw error;
      setEquipment(data || []);
    } catch (err) {
      console.error('Error fetching equipment:', err);
      setError('Failed to load equipment.');
    }
  };

  const fetchEquipmentDetails = async () => {
    if (!selectedEquipment) return;
    try {
      const { data, error } = await supabase
        .from('equipment_details')
        .select('*')
        .eq('equipment_id', selectedEquipment.id)
        .single();
      if (error && error.code !== 'PGRST116') throw error;
      if (data) {
        setEquipmentDetails(data);
        setDetailsFormData({
          power_rating: data.power_rating || '',
          calibration_requirement: data.calibration_requirement || '',
          frequency_of_calibration: data.frequency_of_calibration || '',
          date_of_installation: data.date_of_installation ? dayjs(data.date_of_installation) : null,
          warranty_period: data.warranty_period || '',
          amc_info: data.amc_info || ''
        });
      } else {
        setEquipmentDetails(null);
        setDetailsFormData({
          power_rating: '',
          calibration_requirement: '',
          frequency_of_calibration: '',
          date_of_installation: null,
          warranty_period: '',
          amc_info: ''
        });
      }
    } catch (err) {
      console.error('Error fetching equipment details:', err);
    }
  };

  const fetchMaintenanceLogs = async () => {
    if (!selectedEquipment) return;
    setLoading(true);
    try {
      const { data: logs, error: logsError } = await supabase
        .from('equipment_maintenance_logs')
        .select('*')
        .eq('equipment_id', selectedEquipment.id)
        .order('serial_number', { ascending: false });
      if (logsError) throw logsError;
      
      // Fetch spare parts for each log
      const logsWithParts = await Promise.all((logs || []).map(async (log) => {
        const { data: parts, error: partsError } = await supabase
          .from('equipment_maintenance_spare_parts')
          .select('*')
          .eq('maintenance_log_id', log.id);
        if (partsError) console.error('Error fetching spare parts:', partsError);
        return { ...log, spare_parts: parts || [] };
      }));
      
      setMaintenanceLogs(logsWithParts);
    } catch (err) {
      console.error('Error fetching maintenance logs:', err);
      setError('Failed to load maintenance logs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDepartments();
    fetchCompanyData();
    fetchUserProfile();
  }, [companyId]);

  // Handle department ID from URL
  useEffect(() => {
    if (departmentId && departments.length > 0) {
      const dept = departments.find(d => d.id === departmentId);
      if (dept) {
        setSelectedDepartment(dept);
      }
    } else if (!departmentId) {
      setSelectedDepartment(null);
      setSelectedEquipment(null);
    }
    setSearchTerm(''); // Clear search when changing views
  }, [departmentId, departments]);

  // Handle equipment ID from URL
  useEffect(() => {
    if (equipmentId && equipment.length > 0) {
      const equip = equipment.find(e => e.id === equipmentId);
      if (equip) {
        setSelectedEquipment(equip);
      }
    } else if (!equipmentId) {
      setSelectedEquipment(null);
    }
    setSearchTerm(''); // Clear search when changing views
  }, [equipmentId, equipment]);

  useEffect(() => {
    if (selectedDepartment) {
      fetchEquipment();
    }
  }, [selectedDepartment]);

  useEffect(() => {
    if (selectedEquipment) {
      fetchEquipmentDetails();
      fetchMaintenanceLogs();
    }
  }, [selectedEquipment]);

  const saveEquipmentDetails = async () => {
    if (!selectedEquipment) return;
    
    // Check permissions before proceeding
    if (!hasPermission('locations.equipment_maintenance.edit', 'write')) {
      setSnackbar({
        open: true,
        message: 'You do not have permission to save equipment details.',
        severity: 'error'
      });
      return;
    }
    
    try {
      const detailsData = {
        equipment_id: selectedEquipment.id,
        power_rating: detailsFormData.power_rating.trim() || null,
        calibration_requirement: detailsFormData.calibration_requirement.trim() || null,
        frequency_of_calibration: detailsFormData.frequency_of_calibration.trim() || null,
        date_of_installation: detailsFormData.date_of_installation ? detailsFormData.date_of_installation.format('YYYY-MM-DD') : null,
        warranty_period: detailsFormData.warranty_period.trim() || null,
        amc_info: detailsFormData.amc_info.trim() || null
      };

      if (equipmentDetails) {
        const { error } = await supabase
          .from('equipment_details')
          .update(detailsData)
          .eq('equipment_id', selectedEquipment.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('equipment_details')
          .insert(detailsData);
        if (error) throw error;
      }

      setSnackbar({ open: true, message: 'Equipment details saved successfully', severity: 'success' });
      fetchEquipmentDetails();
    } catch (err) {
      console.error('Error saving equipment details:', err);
      setError('Failed to save equipment details.');
    }
  };

  const calculateTimeTaken = (stoppedAt, restartedAt) => {
    if (!stoppedAt || !restartedAt) return null;
    const diff = dayjs(restartedAt).diff(dayjs(stoppedAt), 'minute');
    return diff;
  };

  const handleOpenDialog = async (log = null) => {
    if (log) {
      setEditMode(true);
      setFormData({
        id: log.id,
        serial_number: log.serial_number,
        log_date: dayjs(log.log_date),
        breakdown_details: log.breakdown_details || '',
        nature_of_breakdown: log.nature_of_breakdown || '',
        stopped_at: log.stopped_at ? dayjs(log.stopped_at) : null,
        restarted_at: log.restarted_at ? dayjs(log.restarted_at) : null,
        spare_parts: (log.spare_parts || []).map(part => ({
          ...part,
          spare_part_description: part.spare_part_description || '',
          cost: part.cost || '',
          invoice_file_path: part.invoice_file_path || '',
          invoice_file_name: part.invoice_file_name || '',
          invoice_file_size: part.invoice_file_size || 0,
          invoice_file_type: part.invoice_file_type || ''
        }))
      });
    } else {
      setEditMode(false);
      try {
        const { data, error } = await supabase
          .rpc('get_next_equipment_maintenance_serial_number', {
            p_company_id: companyId,
            p_equipment_id: selectedEquipment.id
          });
        if (!error && data) {
          setFormData({
            id: '',
            serial_number: data,
            log_date: dayjs(),
            breakdown_details: '',
            nature_of_breakdown: '',
            stopped_at: null,
            restarted_at: null,
            spare_parts: []
          });
        }
      } catch (err) {
        console.error('Error getting serial number:', err);
      }
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditMode(false);
  };

  const addSparePart = () => {
    setFormData(prev => ({
      ...prev,
      spare_parts: [...prev.spare_parts, { 
        spare_part_description: '', 
        cost: '',
        invoice_file_path: '',
        invoice_file_name: '',
        invoice_file_size: 0,
        invoice_file_type: '',
        selectedFile: null
      }]
    }));
  };

  const removeSparePart = async (index) => {
    // Check permissions before proceeding
    if (!hasPermission('locations.equipment_maintenance.delete', 'delete')) {
      setSnackbar({
        open: true,
        message: 'You do not have permission to delete spare parts.',
        severity: 'error'
      });
      return;
    }
    
    const part = formData.spare_parts[index];
    
    // Delete file from storage if it exists
    if (part.invoice_file_path) {
      try {
        await supabase.storage
          .from('equipment-maintenance-invoices')
          .remove([part.invoice_file_path]);
      } catch (err) {
        console.error('Error deleting spare part invoice:', err);
      }
    }
    
    setFormData(prev => ({
      ...prev,
      spare_parts: prev.spare_parts.filter((_, i) => i !== index)
    }));
  };

  const updateSparePart = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      spare_parts: prev.spare_parts.map((part, i) => 
        i === index ? { ...part, [field]: value } : part
      )
    }));
  };

  const handleSparePartFileUpload = async (index, file) => {
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      setSnackbar({ open: true, message: 'File size must be less than 10MB', severity: 'error' });
      return;
    }
    
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      setSnackbar({ open: true, message: 'Only PDF and image files are allowed', severity: 'error' });
      return;
    }
    
    try {
      setSubmitting(true);
      
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
      const filePath = `${companyId}/${fileName}`;

      const { data, error } = await supabase.storage
        .from('equipment-maintenance-invoices')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      updateSparePart(index, 'invoice_file_path', data.path);
      updateSparePart(index, 'invoice_file_name', file.name);
      updateSparePart(index, 'invoice_file_size', file.size);
      updateSparePart(index, 'invoice_file_type', file.type);
      
      setSnackbar({ open: true, message: 'Invoice uploaded successfully', severity: 'success' });
    } catch (err) {
      console.error('Error uploading file:', err);
      setSnackbar({ open: true, message: 'Failed to upload invoice', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveSparePartFile = async (index) => {
    // Check permissions before proceeding
    if (!hasPermission('locations.equipment_maintenance.delete', 'delete')) {
      setSnackbar({
        open: true,
        message: 'You do not have permission to delete spare part files.',
        severity: 'error'
      });
      return;
    }
    
    const part = formData.spare_parts[index];
    
    if (part.invoice_file_path) {
      try {
        await supabase.storage
          .from('equipment-maintenance-invoices')
          .remove([part.invoice_file_path]);
      } catch (err) {
        console.error('Error deleting file:', err);
      }
    }
    
    updateSparePart(index, 'invoice_file_path', '');
    updateSparePart(index, 'invoice_file_name', '');
    updateSparePart(index, 'invoice_file_size', 0);
    updateSparePart(index, 'invoice_file_type', '');
  };

  const viewSparePartInvoice = async (filePath, fileName) => {
    try {
      const { data, error } = await supabase.storage
        .from('equipment-maintenance-invoices')
        .download(filePath);

      if (error) throw error;

      const blobUrl = URL.createObjectURL(data);
      const newWindow = window.open(blobUrl, '_blank');
      
      if (!newWindow) {
        // If popup blocked, download instead
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
      console.error('Error viewing invoice:', err);
      setSnackbar({ open: true, message: 'Failed to load invoice file', severity: 'error' });
    }
  };

  const handleSubmit = async () => {
    if (!selectedEquipment) return;
    
    // Check permissions before proceeding
    if (!hasPermission('locations.equipment_maintenance.edit', 'write')) {
      setError('You do not have permission to create or edit maintenance logs.');
      return;
    }
    
    setSubmitting(true);
    setError(null);

    try {
      const timeTaken = calculateTimeTaken(formData.stopped_at, formData.restarted_at);
      
      const logData = {
        company_id: companyId,
        equipment_id: selectedEquipment.id,
        serial_number: parseInt(formData.serial_number),
        log_date: formData.log_date ? formData.log_date.format('YYYY-MM-DD') : null,
        breakdown_details: formData.breakdown_details.trim() || null,
        nature_of_breakdown: formData.nature_of_breakdown.trim() || null,
        stopped_at: formData.stopped_at ? formData.stopped_at.toISOString() : null,
        restarted_at: formData.restarted_at ? formData.restarted_at.toISOString() : null,
        time_taken_minutes: timeTaken
      };

      let logId;
      if (editMode) {
        const { error } = await supabase
          .from('equipment_maintenance_logs')
          .update({ ...logData, updated_at: new Date().toISOString() })
          .eq('id', formData.id);
        if (error) throw error;
        logId = formData.id;

        // Delete existing spare parts
        await supabase
          .from('equipment_maintenance_spare_parts')
          .delete()
          .eq('maintenance_log_id', logId);
      } else {
        const { data, error } = await supabase
          .from('equipment_maintenance_logs')
          .insert(logData)
          .select()
          .single();
        if (error) throw error;
        logId = data.id;
      }

      // Insert spare parts
      if (formData.spare_parts.length > 0) {
        const spareParts = formData.spare_parts
          .filter(part => part.spare_part_description.trim())
          .map(part => ({
            maintenance_log_id: logId,
            spare_part_description: part.spare_part_description.trim(),
            cost: part.cost ? parseFloat(part.cost) : null,
            invoice_file_path: part.invoice_file_path || null,
            invoice_file_name: part.invoice_file_name || null,
            invoice_file_type: part.invoice_file_type || null,
            invoice_file_size: part.invoice_file_size || null
          }));

        if (spareParts.length > 0) {
          const { error } = await supabase
            .from('equipment_maintenance_spare_parts')
            .insert(spareParts);
          if (error) throw error;
        }
      }

      await fetchMaintenanceLogs();
      handleCloseDialog();
      setSnackbar({
        open: true,
        message: `Maintenance log ${editMode ? 'updated' : 'added'} successfully`,
        severity: 'success'
      });
    } catch (err) {
      console.error('Error saving maintenance log:', err);
      setError(`Failed to ${editMode ? 'update' : 'create'} maintenance log.`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (log) => {
    setLogToDelete(log);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!logToDelete) return;
    
    // Check permissions before proceeding
    if (!hasPermission('locations.equipment_maintenance.delete', 'delete')) {
      setSnackbar({
        open: true,
        message: 'You do not have permission to delete maintenance logs.',
        severity: 'error'
      });
      setDeleteConfirmOpen(false);
      setLogToDelete(null);
      return;
    }
    
    try {
      // Delete all invoice files from storage bucket if they exist
      if (logToDelete.spare_parts && logToDelete.spare_parts.length > 0) {
        const filesToDelete = logToDelete.spare_parts
          .filter(part => part.invoice_file_path)
          .map(part => part.invoice_file_path);
        
        if (filesToDelete.length > 0) {
          try {
            await supabase.storage
              .from('equipment-maintenance-invoices')
              .remove(filesToDelete);
          } catch (storageErr) {
            console.error('Error deleting invoice files from storage:', storageErr);
            // Continue with deletion even if storage deletion fails
          }
        }
      }
      
      // Delete the maintenance log entry
      const { error } = await supabase
        .from('equipment_maintenance_logs')
        .delete()
        .eq('id', logToDelete.id);
      if (error) throw error;
      await fetchMaintenanceLogs();
      setSnackbar({ open: true, message: 'Maintenance log deleted successfully', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: `Error deleting log: ${err.message}`, severity: 'error' });
    }
    setDeleteConfirmOpen(false);
    setLogToDelete(null);
  };

  const handleReviewLog = (log) => {
    setApprovalLog(log);
    setSignatureName('');
    setApprovalNotes('');
    setApprovalDialogOpen(true);
  };

  const handleApprovalSubmit = async () => {
    try {
      // Check permissions before proceeding
      if (!hasPermission('locations.equipment_maintenance.approve', 'write')) {
        setSnackbar({
          open: true,
          message: 'You do not have permission to approve maintenance logs.',
          severity: 'error'
        });
        return;
      }
      
      if (!signatureName.trim()) {
        setSnackbar({ open: true, message: 'Signature name is required for approval', severity: 'error' });
        return;
      }

      const approvalData = {
        approved_by_signature: signatureName,
        approved_at: new Date().toISOString(),
        approval_notes: approvalNotes || null,
        status: 'approved'
      };

      const { error } = await supabase
        .from('equipment_maintenance_logs')
        .update(approvalData)
        .eq('id', approvalLog.id);

      if (error) throw error;

      setApprovalDialogOpen(false);
      setSignatureName('');
      setApprovalNotes('');
      setApprovalLog(null);
      await fetchMaintenanceLogs();
      setSnackbar({ open: true, message: 'Maintenance log approved successfully', severity: 'success' });
    } catch (err) {
      console.error('Error approving log:', err);
      setSnackbar({ open: true, message: `Error approving log: ${err.message}`, severity: 'error' });
    }
  };

  const handleUnapproveLog = async (log) => {
    try {
      // Check permissions before proceeding - need approval permission to unapprove
      if (!hasPermission('locations.equipment_maintenance.approve', 'write')) {
        setSnackbar({
          open: true,
          message: 'You do not have permission to unapprove maintenance logs.',
          severity: 'error'
        });
        return;
      }
      
      const unapprovalData = {
        approved_by_signature: null,
        approved_at: null,
        approval_notes: null,
        status: 'completed'
      };

      const { error } = await supabase
        .from('equipment_maintenance_logs')
        .update(unapprovalData)
        .eq('id', log.id);

      if (error) throw error;

      await fetchMaintenanceLogs();
      setSnackbar({ open: true, message: 'Maintenance log unapproved successfully', severity: 'success' });
    } catch (err) {
      console.error('Error unapproving log:', err);
      setSnackbar({ open: true, message: `Error unapproving log: ${err.message}`, severity: 'error' });
    }
  };

  const fetchUserProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data, error } = await supabase
          .from('profiles')
          .select('time_format_preference')
          .eq('id', user.id)
          .single();
        
        if (error) throw error;
        
        if (data) {
          setUserProfile(data);
          setTimeFormat(data.time_format_preference || '24hour');
        }
      }
    } catch (err) {
      console.error('Error fetching user profile:', err);
    }
  };

  const updateTimeFormat = async (newFormat) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { error } = await supabase
          .from('profiles')
          .update({ time_format_preference: newFormat })
          .eq('id', user.id);
        
        if (error) throw error;
        
        setTimeFormat(newFormat);
        setUserProfile(prev => ({ ...prev, time_format_preference: newFormat }));
      }
    } catch (err) {
      console.error('Error updating time format:', err);
    }
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return dayjs(date).format('MMM DD, YYYY');
  };

  const formatDateTime = (date) => {
    if (!date) return '—';
    const dateObj = new Date(date);
    const dateFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    
    const timeFormatOptions = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: timeFormat === '12hour'
    };
    
    return `${dateObj.toLocaleDateString('en-US', dateFormatOptions)} at ${dateObj.toLocaleTimeString('en-US', timeFormatOptions)}`;
  };

  const formatCurrency = (value) => {
    if (!value) return '—';
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
  };

  const formatTimeTaken = (minutes) => {
    if (!minutes || minutes === 0) return '—';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins} min`;
    if (mins === 0) return `${hours} hr`;
    return `${hours} hr ${mins} min`;
  };

  const getFilteredDepartments = () => {
    if (!departmentSearchTerm) return departments;
    return departments.filter(d => d.name.toLowerCase().includes(departmentSearchTerm.toLowerCase()));
  };

  const getFilteredEquipment = () => {
    if (!equipmentSearchTerm) return equipment;
    return equipment.filter(e => e.equipment_name.toLowerCase().includes(equipmentSearchTerm.toLowerCase()));
  };

  // Filter departments and equipment based on search term
  useEffect(() => {
    if (!selectedDepartment) {
      // Viewing departments
      if (!searchTerm.trim()) {
        setFilteredDepartments(departments);
        return;
      }
      const searchLower = searchTerm.toLowerCase();
      const filtered = departments.filter(dept => 
        dept.name?.toLowerCase().includes(searchLower) ||
        dept.description?.toLowerCase().includes(searchLower)
      );
      setFilteredDepartments(filtered);
      setPage(0);
    } else if (selectedDepartment && !selectedEquipment) {
      // Viewing equipment
      if (!searchTerm.trim()) {
        setFilteredEquipment(equipment);
        return;
      }
      const searchLower = searchTerm.toLowerCase();
      const filtered = equipment.filter(equip => 
        equip.equipment_name?.toLowerCase().includes(searchLower) ||
        equip.equipment_number?.toLowerCase().includes(searchLower) ||
        equip.make?.toLowerCase().includes(searchLower) ||
        equip.remarks?.toLowerCase().includes(searchLower)
      );
      setFilteredEquipment(filtered);
      setPage(0);
    }
  }, [searchTerm, equipment, departments, selectedDepartment, selectedEquipment]);

  const toggleRowExpansion = (logId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(logId)) {
      newExpanded.delete(logId);
    } else {
      newExpanded.add(logId);
    }
    setExpandedRows(newExpanded);
  };

  // Permission check
  if (!hasPermission('locations.equipment_maintenance.view')) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <BuildIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Equipment Maintenance Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access Equipment Maintenance. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }
  
  // Check permissions
  const canEdit = hasPermission('locations.equipment_maintenance.edit', 'write');
  const canDelete = hasPermission('locations.equipment_maintenance.delete', 'delete');
  const canApprove = hasPermission('locations.equipment_maintenance.approve', 'write');

  if (loading && !selectedEquipment) {
    return (
      <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 4, pb: 16 }}>
        <Card sx={{ 
          borderRadius: 3,
          mt: 10,
          mx: 'auto',
          maxWidth: 600,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          boxShadow: `0 12px 32px ${alpha('#3F51B5', 0.15)}`,
        }}>
          <CardContent sx={{ p: 6, textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{
                display: 'inline-flex',
                p: 3,
                borderRadius: '50%',
                bgcolor: alpha('#3F51B5', 0.1),
                mb: 2
              }}>
                <BuildIcon sx={{ fontSize: 48, color: '#3F51B5' }} />
              </Box>
            </Box>
            <Typography variant="h6" sx={{
              fontWeight: 500,
              color: '#3F51B5',
              mb: 1,
              fontSize: '1.1rem'
            }}>
              Loading Equipment Maintenance
            </Typography>
            <Typography variant="body2" sx={{ 
              color: 'text.secondary',
              fontWeight: 500,
              maxWidth: 400,
              mx: 'auto',
              lineHeight: 1.6,
              mb: 3
            }}>
              Analyzing equipment maintenance data...
            </Typography>
            <Box sx={{ 
              width: '60%', 
              mx: 'auto',
              '& .MuiLinearProgress-root': {
                height: 8,
                borderRadius: 4,
                bgcolor: alpha(theme.palette.grey[300], 0.3),
                '& .MuiLinearProgress-bar': {
                  bgcolor: '#3F51B5',
                  borderRadius: 4
                }
              }
            }}>
              <LinearProgress />
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Fade in timeout={800}>
        <Box>
          <Container maxWidth="xl" sx={{ py: 3, pb: 16 }}>
            {/* Header Section */}
            <Box sx={{ mb: 3 }}>
              {/* Enhanced Page Header */}
              <Card sx={{
                borderRadius: 3,
                mt: 5,
                mb: 3,
                border: `1px solid ${theme.palette.grey[400]}`,
                borderTop: '3px solid #3F51B5',
                background: theme.palette.grey[100],
                backdropFilter: 'blur(20px)',
                boxShadow: 'none',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 6px 20px ${alpha('#3F51B5', 0.15)}`,
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2 }}>
                    {(selectedDepartment || selectedEquipment) && (
                      <Tooltip title={selectedEquipment ? "Back to Equipment List" : "Back to Departments"}>
                        <IconButton
                          onClick={() => {
                            setSearchTerm('');
                            setPage(0);
                            if (selectedEquipment) {
                              navigate(`/company/${companyId}/equipment-maintenance/${selectedDepartment.id}`);
                            } else {
                              navigate(`/company/${companyId}/equipment-maintenance`);
                            }
                          }}
                          sx={{
                            bgcolor: 'white',
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            borderRadius: 2,
                            transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                            '&:hover': {
                              bgcolor: 'primary.main',
                              color: 'white',
                              transform: 'scale(1.05)',
                              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                            }
                          }}
                        >
                          <ArrowBackIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Box sx={{
                      p: 2,
                      borderRadius: 3,
                      background: `linear-gradient(${alpha('#3F51B5', 0.08)}, ${alpha('#3F51B5', 0.04)})`,
                      border: `1px solid ${alpha('#3F51B5', 0.15)}`
                    }}>
                      <BuildIcon sx={{ fontSize: 32, color: '#3F51B5' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h4" sx={{
                        fontWeight: 500,
                        color: '#3F51B5',
                        mb: 1,
                        fontSize: '2rem',
                        letterSpacing: '-0.5px'
                      }}>
                        {selectedEquipment 
                          ? selectedEquipment.equipment_name 
                          : selectedDepartment 
                            ? `${selectedDepartment.name} - Equipment` 
                            : 'Equipment Maintenance'}
                      </Typography>
                      <Typography variant="subtitle1" sx={{ 
                        color: 'text.secondary', 
                        fontWeight: 500,
                        fontSize: '1rem'
                      }}>
                        {companyName}
                      </Typography>
                    </Box>
                    
                    {/* Time Format Selector */}
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                      <InputLabel sx={{ fontSize: '0.9rem' }}>Time</InputLabel>
                      <Select
                        value={timeFormat}
                        label="Time"
                        onChange={(e) => updateTimeFormat(e.target.value)}
                        sx={{
                          borderRadius: 2,
                          backgroundColor: 'white',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderWidth: '1px',
                            borderColor: alpha(theme.palette.grey[400], 0.8)
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderWidth: '1px',
                            borderColor: theme.palette.primary.main
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderWidth: '1px',
                            borderColor: theme.palette.primary.main
                          }
                        }}
                      >
                        <MenuItem value="12hour">12 Hour</MenuItem>
                        <MenuItem value="24hour">24 Hour</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Typography variant="body2" sx={{ 
                    color: 'text.secondary', 
                    maxWidth: 700, 
                    lineHeight: 1.6,
                    fontSize: '0.95rem',
                    fontWeight: 500
                  }}>
                    {selectedEquipment 
                      ? 'View equipment details and track maintenance logs with repair history and spare parts.'
                      : selectedDepartment 
                        ? 'Select equipment to view and manage maintenance logs.'
                        : 'Track maintenance logs, repair history, and spare parts for all equipment.'}
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

            {/* Search Bar - Only show when viewing departments or equipment */}
            {!selectedEquipment && (
              <Card sx={{
                height: 100,
                mb: 2,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
                boxShadow: `0 8px 24px ${alpha(theme.palette.grey[500], 0.12)}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                }
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField
                      placeholder={!selectedDepartment ? 'Search departments...' : 'Search equipment...'}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        minWidth: 280,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          background: 'white',
                          height: 40,
                          '& fieldset': { border: 'none' },
                          '&:hover': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                          }
                        },
                        '& .MuiInputBase-input': {
                          fontSize: '0.9rem',
                          fontWeight: 500,
                          py: 1.25
                        }
                      }}
                    />
                    
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      {!selectedDepartment 
                        ? `${filteredDepartments.length} department${filteredDepartments.length !== 1 ? 's' : ''}`
                        : `${filteredEquipment.length} equipment item${filteredEquipment.length !== 1 ? 's' : ''}`
                      }
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}

            {!selectedDepartment && (
              departments.length === 0 ? (
                <Paper 
                  sx={{
                    textAlign: 'center',
                    py: 8,
                    px: 4,
                    border: '2px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                  }}
                >
                  <Box sx={{ 
                    p: 2.5, 
                    borderRadius: 3, 
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    backdropFilter: 'blur(10px)',
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
                    mb: 3,
                    width: 'fit-content',
                    mx: 'auto'
                  }}>
                    <BuildIcon sx={{ fontSize: 36, color: 'text.secondary' }} />
                  </Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                    No Departments Available
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto', fontWeight: 500 }}>
                    Please create departments in Settings to manage equipment maintenance.
                  </Typography>
                </Paper>
              ) : (
                <Paper sx={{ 
                  borderRadius: 3, 
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
                  boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                  overflow: 'hidden',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                  }
                }}>
                  <TableContainer sx={{ overflow: 'visible' }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell 
                            sx={{ 
                              fontWeight: 500,
                              fontSize: '0.9rem',
                              bgcolor: 'white',
                              borderBottom: `2px solid ${theme.palette.primary.main}`,
                              color: 'primary.main',
                              py: 2,
                              px: 3
                            }}
                          >
                            Department Name
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredDepartments.map((department) => (
                          <TableRow 
                            key={department.id} 
                            hover
                            sx={{ 
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              '&:hover': { 
                                bgcolor: 'grey.50',
                                transform: 'translateX(4px)',
                                transition: 'all 0.2s ease',
                                boxShadow: 'inset 4px 0 0 black'
                              },
                              '& .MuiTableCell-root': {
                                borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                                py: 3,
                                px: 3
                              }
                            }}
                            onClick={() => navigate(`/company/${companyId}/equipment-maintenance/${department.id}`)}
                          >
                            <TableCell>
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  fontWeight: 400,
                                  color: 'text.primary',
                                  fontSize: '1.05rem'
                                }}
                              >
                                {department.name}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )
            )}

            {selectedDepartment && !selectedEquipment && (
              equipment.length === 0 ? (
                <Paper 
                  sx={{
                    textAlign: 'center',
                    py: 8,
                    px: 4,
                    border: '2px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                  }}
                >
                  <Box sx={{ 
                    p: 2.5, 
                    borderRadius: 3, 
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    backdropFilter: 'blur(10px)',
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
                    mb: 3,
                    width: 'fit-content',
                    mx: 'auto'
                  }}>
                    <BuildIcon sx={{ fontSize: 36, color: 'text.secondary' }} />
                  </Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                    No Equipment Available
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto', fontWeight: 500 }}>
                    No equipment found in this department. Add equipment from the Equipment List page.
                  </Typography>
                </Paper>
              ) : (
                <Paper sx={{ 
                  borderRadius: 3, 
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
                  boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                  overflow: 'hidden',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                  }
                }}>
                  <TableContainer sx={{ overflow: 'visible' }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell 
                            sx={{ 
                              fontWeight: 500,
                              fontSize: '0.9rem',
                              bgcolor: 'white',
                              borderBottom: `2px solid ${theme.palette.primary.main}`,
                              color: 'primary.main',
                              py: 2,
                              px: 3
                            }}
                          >
                            Equipment Name
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredEquipment.map((equip) => (
                          <TableRow 
                            key={equip.id} 
                            hover
                            sx={{ 
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              '&:hover': { 
                                bgcolor: 'grey.50',
                                transform: 'translateX(4px)',
                                transition: 'all 0.2s ease',
                                boxShadow: 'inset 4px 0 0 black'
                              },
                              '& .MuiTableCell-root': {
                                borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                                py: 3,
                                px: 3
                              }
                            }}
                            onClick={() => navigate(`/company/${companyId}/equipment-maintenance/${selectedDepartment.id}/${equip.id}`)}
                          >
                            <TableCell>
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  fontWeight: 400,
                                  color: 'text.primary',
                                  fontSize: '1.05rem'
                                }}
                              >
                                {equip.equipment_name}
                              </Typography>
                              {equip.equipment_number && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                  {equip.equipment_number}
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )
            )}

            {selectedEquipment && (
              <>
                {/* Equipment Details Section */}
                <Card sx={{ mb: 3, borderRadius: 3, p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 500 }}>Equipment Details</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        label="Equipment Name/Description"
                        value={selectedEquipment.equipment_name}
                        disabled
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Make & Model"
                        value={selectedEquipment.make || ''}
                        disabled
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Serial Number/Tag No."
                        value={selectedEquipment.serial_number || ''}
                        disabled
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Capacity"
                        value={selectedEquipment.rated_capacity || ''}
                        disabled
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Power Rating"
                        value={detailsFormData.power_rating}
                        onChange={(e) => setDetailsFormData(prev => ({ ...prev, power_rating: e.target.value }))}
                        disabled={!canEdit}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                  </Grid>
                  
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>Calibration</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        label="Calibration Requirement Details"
                        value={detailsFormData.calibration_requirement}
                        onChange={(e) => setDetailsFormData(prev => ({ ...prev, calibration_requirement: e.target.value }))}
                        disabled={!canEdit}
                        fullWidth
                        multiline
                        rows={2}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Frequency of Calibration"
                        value={detailsFormData.frequency_of_calibration}
                        onChange={(e) => setDetailsFormData(prev => ({ ...prev, frequency_of_calibration: e.target.value }))}
                        disabled={!canEdit}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <DateTimePicker
                        label="Date of Installation"
                        value={detailsFormData.date_of_installation}
                        onChange={(newValue) => setDetailsFormData(prev => ({ ...prev, date_of_installation: newValue }))}
                        disabled={!canEdit}
                        ampm={timeFormat === '12hour'}
                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Warranty Period"
                        value={detailsFormData.warranty_period}
                        onChange={(e) => setDetailsFormData(prev => ({ ...prev, warranty_period: e.target.value }))}
                        disabled={!canEdit}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="AMC/Service Contract Info"
                        value={detailsFormData.amc_info}
                        onChange={(e) => setDetailsFormData(prev => ({ ...prev, amc_info: e.target.value }))}
                        disabled={!canEdit}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                  </Grid>
                  
                  <Box sx={{ mt: 3, textAlign: 'right' }}>
                    <Tooltip title={canEdit ? "" : "No permission to save equipment details"}>
                      <span>
                        <Button 
                          variant="contained" 
                          disabled={!canEdit}
                          onClick={saveEquipmentDetails}
                        >
                          Save Equipment Details
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>
                </Card>

                {/* Maintenance Logs Table */}
                <Paper sx={{ 
                  borderRadius: 3, 
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
                  boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                  overflow: 'hidden',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                  }
                }}>
                  <TableContainer sx={{ overflow: 'visible' }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500, fontSize: '0.9rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 3 }}>S.No</TableCell>
                          <TableCell sx={{ fontWeight: 500, fontSize: '0.9rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 3 }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 500, fontSize: '0.9rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 3 }}>Breakdown Details</TableCell>
                          <TableCell sx={{ fontWeight: 500, fontSize: '0.9rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 3 }}>Nature</TableCell>
                          <TableCell sx={{ fontWeight: 500, fontSize: '0.9rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 3 }}>Time Taken</TableCell>
                          <TableCell sx={{ fontWeight: 500, fontSize: '0.9rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 3 }}>Approved By</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 500, fontSize: '0.9rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 3, width: 120 }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {maintenanceLogs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((log, index) => {
                          const isExpanded = expandedRows.has(log.id);
                          const totalCost = log.spare_parts.reduce((sum, part) => sum + (parseFloat(part.cost) || 0), 0);
                          
                          return (
                            <React.Fragment key={`log-row-${log.id}-${index}`}>
                              {/* Main log row */}
                              <TableRow 
                                hover
                                sx={{ 
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  bgcolor: 'white',
                                  '&:hover': { 
                                    bgcolor: 'grey.50',
                                    transform: 'translateX(4px)',
                                    transition: 'all 0.2s ease',
                                    boxShadow: 'inset 4px 0 0 black'
                                  },
                                  '& .MuiTableCell-root': {
                                    borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                                    py: 3,
                                    px: 3
                                  }
                                }}
                              >
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    {log.spare_parts.length > 0 && (
                                      <IconButton
                                        size="medium"
                                        onClick={() => toggleRowExpansion(log.id)}
                                        sx={{ 
                                          color: 'primary.main',
                                          border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                          borderRadius: 2,
                                          transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                          '&:hover': {
                                            bgcolor: 'primary.main',
                                            color: 'white',
                                            transform: 'scale(1.05)',
                                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                                          }
                                        }}
                                      >
                                        {isExpanded ? <ExpandLessIcon fontSize="medium" /> : <ExpandMoreIcon fontSize="medium" />}
                                      </IconButton>
                                    )}
                                    <Typography variant="h6" sx={{ fontWeight: 400, color: 'text.primary', fontSize: '1.1rem' }}>
                                      {log.serial_number}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="h6" sx={{ fontWeight: 400, color: 'text.primary', fontSize: '1rem' }}>
                                    {formatDate(log.log_date)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 400, color: 'text.primary', fontSize: '0.9rem' }}>
                                    {log.breakdown_details || '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 400, color: 'text.primary', fontSize: '0.9rem' }}>
                                    {log.nature_of_breakdown || '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="h6" sx={{ fontWeight: 400, color: 'text.primary', fontSize: '1rem' }}>
                                    {formatTimeTaken(log.time_taken_minutes)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {log.status === 'approved' && log.approved_by_signature ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <VerifiedIcon sx={{ color: 'success.main', fontSize: '1.2rem' }} />
                                      <Box>
                                        <Typography variant="body2" sx={{ 
                                          fontWeight: 500, 
                                          color: 'text.primary',
                                          fontSize: '0.9rem'
                                        }}>
                                          {log.approved_by_signature}
                                        </Typography>
                                        {log.approved_at && (
                                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                            {dayjs(log.approved_at).format('MMM D, YYYY')}
                                          </Typography>
                                        )}
                                      </Box>
                                    </Box>
                                  ) : (
                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                                      —
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell align="center">
                                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                    <Tooltip title={canEdit ? "Edit Log" : "No permission to edit"}>
                                      <span>
                                        <IconButton 
                                          size="medium"
                                          disabled={!canEdit}
                                          onClick={() => handleOpenDialog(log)}
                                          sx={{
                                            color: 'primary.main',
                                            border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                            borderRadius: 2,
                                            transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                            '&:hover:not(:disabled)': {
                                              bgcolor: 'primary.main',
                                              color: 'white',
                                              transform: 'scale(1.05)',
                                              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                                            },
                                            '&.Mui-disabled': {
                                              color: alpha(theme.palette.primary.main, 0.3),
                                              border: `1px solid ${alpha(theme.palette.grey[400], 0.2)}`,
                                              cursor: 'not-allowed'
                                            }
                                          }}
                                        >
                                          <EditIcon fontSize="medium" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title={canDelete ? "Delete Log" : "No permission to delete"}>
                                      <span>
                                        <IconButton 
                                          size="medium"
                                          disabled={!canDelete}
                                          onClick={() => handleDeleteClick(log)}
                                          sx={{
                                            color: 'error.main',
                                            border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                            borderRadius: 2,
                                            transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                            '&:hover:not(:disabled)': {
                                              bgcolor: 'error.main',
                                              color: 'white',
                                              transform: 'scale(1.05)',
                                              boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}`
                                            },
                                            '&.Mui-disabled': {
                                              color: alpha(theme.palette.error.main, 0.3),
                                              border: `1px solid ${alpha(theme.palette.grey[400], 0.2)}`,
                                              cursor: 'not-allowed'
                                            }
                                          }}
                                        >
                                          <DeleteIcon fontSize="medium" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    {log.status !== 'approved' ? (
                                      <Tooltip title={canApprove ? "Approve Log" : "No permission to approve"}>
                                        <span>
                                          <IconButton 
                                            size="medium"
                                            disabled={!canApprove}
                                            onClick={() => handleReviewLog(log)}
                                            sx={{
                                              color: 'success.main',
                                              border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                              borderRadius: 2,
                                              transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                              '&:hover:not(:disabled)': {
                                                bgcolor: 'success.main',
                                                color: 'white',
                                                transform: 'scale(1.05)',
                                                boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}`
                                              },
                                              '&.Mui-disabled': {
                                                color: alpha(theme.palette.success.main, 0.3),
                                                border: `1px solid ${alpha(theme.palette.grey[400], 0.2)}`,
                                                cursor: 'not-allowed'
                                              }
                                            }}
                                          >
                                            <CheckIcon fontSize="medium" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    ) : (
                                      <Tooltip title={canApprove ? "Unapprove Log" : "No permission to unapprove"}>
                                        <span>
                                          <IconButton 
                                            size="medium"
                                            disabled={!canApprove}
                                            onClick={() => handleUnapproveLog(log)}
                                            sx={{
                                              color: 'warning.main',
                                              border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                              borderRadius: 2,
                                              transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                              '&:hover:not(:disabled)': {
                                                bgcolor: 'warning.main',
                                                color: 'white',
                                                transform: 'scale(1.05)',
                                                boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.3)}`
                                              },
                                              '&.Mui-disabled': {
                                                color: alpha(theme.palette.warning.main, 0.3),
                                                border: `1px solid ${alpha(theme.palette.grey[400], 0.2)}`,
                                                cursor: 'not-allowed'
                                              }
                                            }}
                                          >
                                            <CancelIcon fontSize="medium" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>

                              {/* Expanded details */}
                              {isExpanded && log.spare_parts.length > 0 && (
                                <TableRow>
                                  <TableCell colSpan={7} sx={{ p: 0, border: 'none' }}>
                                    <Box sx={{ 
                                      borderTop: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                                      bgcolor: alpha(theme.palette.grey[50], 0.8),
                                      p: 6,
                                      mb: 8,
                                      mt: 3,
                                      mx: 2,
                                      borderRadius: 3,
                                      border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                      '&:hover': {
                                        bgcolor: alpha(theme.palette.primary.main, 0.02),
                                        transform: 'translateY(-1px)',
                                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.06)}`
                                      }
                                    }}>
                                      <Typography variant="h6" sx={{ 
                                        fontWeight: 500, 
                                        color: 'primary.main', 
                                        mb: 4,
                                        fontSize: '1.3rem'
                                      }}>
                                        Maintenance Details
                                      </Typography>
                                      
                                      <Grid container spacing={3} sx={{ mb: 4 }}>
                                        <Grid item xs={12} md={4}>
                                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 1 }}>
                                            Stopped At
                                          </Typography>
                                          <Typography variant="body1" sx={{ fontWeight: 400, fontSize: '0.9rem' }}>
                                            {formatDateTime(log.stopped_at)}
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 1 }}>
                                            Restarted At
                                          </Typography>
                                          <Typography variant="body1" sx={{ fontWeight: 400, fontSize: '0.9rem' }}>
                                            {formatDateTime(log.restarted_at)}
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 1 }}>
                                            Total Cost
                                          </Typography>
                                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1.1rem', color: 'primary.main' }}>
                                            ${formatCurrency(totalCost)}
                                          </Typography>
                                        </Grid>
                                      </Grid>

                                      {/* Approval Information */}
                                      {log.status === 'approved' && log.approved_by_signature && (
                                        <>
                                          <Divider sx={{ my: 3 }} />
                                          <Card sx={{ 
                                            p: 3, 
                                            border: `1px solid ${theme.palette.success.main}`, 
                                            borderRadius: 3, 
                                            backgroundColor: alpha(theme.palette.success.main, 0.05),
                                            boxShadow: `0 4px 20px ${alpha(theme.palette.success.main, 0.15)}`,
                                            mb: 3
                                          }}>
                                            <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: 'success.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                                              <VerifiedIcon />
                                              Approval Information
                                            </Typography>
                                            <Grid container spacing={2}>
                                              <Grid item xs={12} md={6}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 1 }}>
                                                  Approved By
                                                </Typography>
                                                <Box sx={{ 
                                                  border: `1px solid ${theme.palette.success.main}`, 
                                                  p: 2, 
                                                  borderRadius: 2, 
                                                  bgcolor: 'white'
                                                }}>
                                                  <Box sx={{ 
                                                    fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                                                    fontSize: '20px', 
                                                    color: theme.palette.text.primary,
                                                    textShadow: '1px 1px 1px rgba(0,0,0,0.1)',
                                                    mb: 1
                                                  }}>
                                                    {log.approved_by_signature}
                                                  </Box>
                                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                                    Electronic Signature
                                                  </Typography>
                                                </Box>
                                              </Grid>
                                              <Grid item xs={12} md={6}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 1 }}>
                                                  Approved At
                                                </Typography>
                                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                  {log.approved_at ? formatDateTime(log.approved_at) : 'N/A'}
                                                </Typography>
                                              </Grid>
                                              {log.approval_notes && (
                                                <Grid item xs={12}>
                                                  <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 1 }}>
                                                    Approval Notes
                                                  </Typography>
                                                  <Typography variant="body1" sx={{ fontWeight: 400 }}>
                                                    {log.approval_notes}
                                                  </Typography>
                                                </Grid>
                                              )}
                                            </Grid>
                                          </Card>
                                        </>
                                      )}

                                      <Divider sx={{ my: 3 }} />
                                      <Typography variant="h6" sx={{ 
                                        fontWeight: 500, 
                                        color: 'primary.main', 
                                        mb: 3,
                                        fontSize: '1.2rem'
                                      }}>
                                        Spare Parts ({log.spare_parts.length})
                                      </Typography>
                                      
                                      <TableContainer component={Paper} sx={{ 
                                        borderRadius: 3, 
                                        border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        overflow: 'hidden',
                                        '&:hover': {
                                          transform: 'translateY(-2px)',
                                          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`,
                                          border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                                        }
                                      }}>
                                        <Table size="medium">
                                          <TableHead>
                                            <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }}>
                                              <TableCell sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1rem', py: 2.5, px: 3 }}>Spare Part Description</TableCell>
                                              <TableCell sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1rem', py: 2.5, px: 3 }} align="right">Cost</TableCell>
                                              <TableCell sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1rem', py: 2.5, px: 3 }} align="center">Invoice</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {log.spare_parts.map((part, idx) => (
                                              <TableRow 
                                                key={idx}
                                                sx={{ 
                                                  transition: 'all 0.2s ease',
                                                  '&:hover': { 
                                                    bgcolor: 'grey.50',
                                                    transform: 'translateX(4px)',
                                                    transition: 'all 0.2s ease',
                                                    boxShadow: 'inset 4px 0 0 black'
                                                  },
                                                  '&:nth-of-type(even)': { bgcolor: alpha(theme.palette.grey[50], 0.3) }
                                                }}
                                              >
                                                <TableCell sx={{ py: 3, px: 3 }}>
                                                  <Typography variant="body1" sx={{ fontWeight: 400, color: 'text.primary', fontSize: '0.95rem' }}>
                                                    {part.spare_part_description}
                                                  </Typography>
                                                </TableCell>
                                                <TableCell align="right" sx={{ py: 3, px: 3 }}>
                                                  <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.95rem', color: 'text.primary' }}>
                                                    ${formatCurrency(part.cost)}
                                                  </Typography>
                                                </TableCell>
                                                <TableCell align="center" sx={{ py: 3, px: 3 }}>
                                                  {part.invoice_file_path ? (
                                                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                                      <Tooltip title="View Invoice">
                                                        <IconButton 
                                                          size="medium"
                                                          onClick={() => viewSparePartInvoice(part.invoice_file_path, part.invoice_file_name || 'Invoice.pdf')}
                                                          sx={{ 
                                                            color: 'info.main',
                                                            border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                                            borderRadius: 2,
                                                            transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                                            '&:hover': {
                                                              bgcolor: 'info.main',
                                                              color: 'white',
                                                              transform: 'scale(1.05)',
                                                              boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.3)}`
                                                            }
                                                          }}
                                                        >
                                                          <PictureAsPdfIcon fontSize="medium" />
                                                        </IconButton>
                                                      </Tooltip>
                                                    </Box>
                                                  ) : (
                                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                                      No invoice
                                                    </Typography>
                                                  )}
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          );
                        })}
                        {maintenanceLogs.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                              <Typography variant="body2" color="text.secondary">
                                No maintenance logs yet. Click the + button to add the first log.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    mt: 4,
                    mb: 6,
                    px: 3
                  }}>
                    <Paper sx={{
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                      boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                      overflow: 'hidden'
                    }}>
                      <TablePagination
                        component="div"
                        count={maintenanceLogs.length}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => {
                          setRowsPerPage(parseInt(e.target.value, 10));
                          setPage(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50]}
                        sx={{ 
                          px: 3,
                          py: 2,
                          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            color: theme.palette.text.primary
                          },
                          '& .MuiTablePagination-select': {
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            borderRadius: 2,
                            px: 2,
                            py: 1,
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            background: 'white',
                            '&:hover': {
                              border: `1px solid ${theme.palette.grey[600]}`,
                              bgcolor: 'white'
                            }
                          },
                          '& .MuiTablePagination-actions': {
                            '& .MuiIconButton-root': {
                              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                              borderRadius: 2,
                              margin: '0 2px',
                              background: 'white',
                              color: theme.palette.primary.main,
                              fontWeight: 500,
                              '&:hover': {
                                border: `1px solid ${theme.palette.grey[600]}`,
                                bgcolor: 'white'
                              },
                              '&.Mui-disabled': {
                                background: alpha(theme.palette.grey[300], 0.5),
                                color: theme.palette.grey[500],
                                border: `1px solid ${theme.palette.grey[300]}`
                              }
                            }
                          }
                        }}
                      />
                    </Paper>
                  </Box>
                </Paper>

                <Tooltip title={canEdit ? "Add Maintenance Log" : "No permission to add"}>
                  <span>
                    <Fab
                      color="primary"
                      disabled={!canEdit}
                      onClick={() => handleOpenDialog()}
                      sx={{ 
                        position: 'fixed', 
                        bottom: 24, 
                        right: 10, 
                        width: 100, 
                        height: 100,
                        '&.Mui-disabled': {
                          bgcolor: alpha(theme.palette.grey[400], 0.3),
                          color: alpha(theme.palette.text.primary, 0.3),
                          cursor: 'not-allowed'
                        }
                      }}
                    >
                      <AddIcon sx={{ fontSize: 32 }} />
                    </Fab>
                  </span>
                </Tooltip>
              </>
            )}

            {/* Department Selection Dialog */}
            <Dialog
              open={departmentDialogOpen}
              onClose={() => {
                setDepartmentDialogOpen(false);
                setDepartmentSearchTerm('');
                setDialogSelectedDepartment(null);
              }}
              fullWidth
              maxWidth="md"
              PaperProps={{
                sx: {
                  borderRadius: 4,
                  height: '70vh',
                  width: '600px',
                  maxWidth: '600px',
                  margin: 'auto',
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
                }
              }}
            >
              <DialogTitle sx={{ 
                pb: 2, 
                fontSize: '1.25rem', 
                fontWeight: 500,
                color: 'text.primary',
                borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
                  Select Department
                </Typography>
                <IconButton onClick={() => {
                  setDepartmentDialogOpen(false);
                  setDepartmentSearchTerm('');
                  setDialogSelectedDepartment(null);
                }} size="small">
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent sx={{ p: 0 }}>
                <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
                  <TextField
                    fullWidth
                    placeholder="Search departments..."
                    value={departmentSearchTerm}
                    onChange={(e) => setDepartmentSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: theme.palette.primary.main }} />
                        </InputAdornment>
                      ),
                      sx: { 
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        '&:hover': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                        },
                        '&.Mui-focused': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                        }
                      }
                    }}
                    size="medium"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          border: 'none'
                        },
                        '&:hover fieldset': {
                          border: 'none'
                        },
                        '&.Mui-focused fieldset': {
                          border: 'none'
                        }
                      }
                    }}
                  />
                </Box>
                
                <Box sx={{ 
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  maxHeight: 'calc(70vh - 220px)',
                  '&::-webkit-scrollbar': {
                    width: '8px'
                  },
                  '&::-webkit-scrollbar-track': {
                    background: alpha(theme.palette.grey[200], 0.5)
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: alpha(theme.palette.grey[400], 0.8),
                    borderRadius: '4px',
                    '&:hover': {
                      background: alpha(theme.palette.grey[500], 0.9)
                    }
                  }
                }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Department</TableCell>
                        <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getFilteredDepartments().map((dept) => (
                        <TableRow 
                          key={dept.id}
                          hover
                          sx={{
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: 'grey.50',
                              transform: 'translateX(4px)',
                              boxShadow: 'inset 4px 0 0 black'
                            },
                            '& .MuiTableCell-root': {
                              borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                              py: 2,
                              pl: 3,
                              pr: 3
                            }
                          }}
                        >
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                                {dept.name}
                              </Typography>
                              {dept.description && (
                                <Typography variant="caption" color="text.secondary">
                                  {dept.description}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setDialogSelectedDepartment(dept)}
                              sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: '0.8rem',
                                px: 2,
                                py: 1,
                                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                bgcolor: dialogSelectedDepartment?.id === dept.id ? 'black' : 'transparent',
                                color: dialogSelectedDepartment?.id === dept.id ? 'white' : 'inherit',
                                '&:hover': {
                                  bgcolor: dialogSelectedDepartment?.id === dept.id ? 'black' : 'transparent',
                                  border: `1px solid ${theme.palette.primary.main}`,
                                  transform: 'translateY(-1px)',
                                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                                }
                              }}
                            >
                              Select
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {getFilteredDepartments().length === 0 && (
                        <TableRow>
                          <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              {departmentSearchTerm ? 'No departments found matching your search.' : 'No departments available.'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </DialogContent>
              <DialogActions sx={{ 
                p: 3, 
                gap: 2, 
                borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                bgcolor: alpha(theme.palette.grey[50], 0.5),
                display: 'flex',
                justifyContent: 'flex-end'
              }}>
                <Button
                  onClick={() => {
                    setDepartmentDialogOpen(false);
                    setDepartmentSearchTerm('');
                    setDialogSelectedDepartment(null);
                  }}
                  variant="outlined"
                  sx={{ 
                    borderRadius: 2, 
                    px: 3, 
                    py: 1, 
                    fontSize: '0.9rem', 
                    fontWeight: 500,
                    textTransform: 'none',
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    '&:hover': {
                      border: `1px solid ${theme.palette.grey[600]}`,
                      transform: 'translateY(-1px)'
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    if (dialogSelectedDepartment) {
                      navigate(`/company/${companyId}/equipment-maintenance/${dialogSelectedDepartment.id}`);
                      setDepartmentDialogOpen(false);
                      setDepartmentSearchTerm('');
                      setDialogSelectedDepartment(null);
                    }
                  }}
                  variant="contained"
                  disabled={!dialogSelectedDepartment}
                  sx={{ 
                    borderRadius: 2, 
                    px: 3, 
                    py: 1, 
                    fontSize: '0.9rem', 
                    fontWeight: 500,
                    textTransform: 'none',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                      transform: 'translateY(-1px)',
                      boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`
                    },
                    '&:disabled': {
                      background: alpha(theme.palette.grey[400], 0.5),
                      color: alpha(theme.palette.grey[600], 0.7)
                    }
                  }}
                >
                  Confirm Selection
                </Button>
              </DialogActions>
            </Dialog>

            {/* Equipment Selection Dialog */}
            <Dialog
              open={equipmentDialogOpen}
              onClose={() => {
                setEquipmentDialogOpen(false);
                setEquipmentSearchTerm('');
                setDialogSelectedEquipment(null);
              }}
              fullWidth
              maxWidth="md"
              PaperProps={{
                sx: {
                  borderRadius: 4,
                  height: '70vh',
                  width: '600px',
                  maxWidth: '600px',
                  margin: 'auto',
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
                }
              }}
            >
              <DialogTitle sx={{ 
                pb: 2, 
                fontSize: '1.25rem', 
                fontWeight: 500,
                color: 'text.primary',
                borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
                  Select Equipment
                </Typography>
                <IconButton onClick={() => {
                  setEquipmentDialogOpen(false);
                  setEquipmentSearchTerm('');
                  setDialogSelectedEquipment(null);
                }} size="small">
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent sx={{ p: 0 }}>
                <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
                  <TextField
                    fullWidth
                    placeholder="Search equipment..."
                    value={equipmentSearchTerm}
                    onChange={(e) => setEquipmentSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: theme.palette.primary.main }} />
                        </InputAdornment>
                      ),
                      sx: { 
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        '&:hover': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                        },
                        '&.Mui-focused': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                        }
                      }
                    }}
                    size="medium"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          border: 'none'
                        },
                        '&:hover fieldset': {
                          border: 'none'
                        },
                        '&.Mui-focused fieldset': {
                          border: 'none'
                        }
                      }
                    }}
                  />
                </Box>
                
                <Box sx={{ 
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  maxHeight: 'calc(70vh - 220px)',
                  '&::-webkit-scrollbar': {
                    width: '8px'
                  },
                  '&::-webkit-scrollbar-track': {
                    background: alpha(theme.palette.grey[200], 0.5)
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: alpha(theme.palette.grey[400], 0.8),
                    borderRadius: '4px',
                    '&:hover': {
                      background: alpha(theme.palette.grey[500], 0.9)
                    }
                  }
                }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Equipment</TableCell>
                        <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getFilteredEquipment().map((equip) => (
                        <TableRow 
                          key={equip.id}
                          hover
                          sx={{
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: 'grey.50',
                              transform: 'translateX(4px)',
                              boxShadow: 'inset 4px 0 0 black'
                            },
                            '& .MuiTableCell-root': {
                              borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                              py: 2,
                              pl: 3,
                              pr: 3
                            }
                          }}
                        >
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                                {equip.equipment_name}
                              </Typography>
                              {equip.equipment_number && (
                                <Typography variant="caption" color="text.secondary">
                                  {equip.equipment_number}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setDialogSelectedEquipment(equip)}
                              sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: '0.8rem',
                                px: 2,
                                py: 1,
                                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                bgcolor: dialogSelectedEquipment?.id === equip.id ? 'black' : 'transparent',
                                color: dialogSelectedEquipment?.id === equip.id ? 'white' : 'inherit',
                                '&:hover': {
                                  bgcolor: dialogSelectedEquipment?.id === equip.id ? 'black' : 'transparent',
                                  border: `1px solid ${theme.palette.primary.main}`,
                                  transform: 'translateY(-1px)',
                                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                                }
                              }}
                            >
                              Select
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {getFilteredEquipment().length === 0 && (
                        <TableRow>
                          <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              {equipmentSearchTerm ? 'No equipment found matching your search.' : 'No equipment available.'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </DialogContent>
              <DialogActions sx={{ 
                p: 3, 
                gap: 2, 
                borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                bgcolor: alpha(theme.palette.grey[50], 0.5),
                display: 'flex',
                justifyContent: 'flex-end'
              }}>
                <Button
                  onClick={() => {
                    setEquipmentDialogOpen(false);
                    setEquipmentSearchTerm('');
                    setDialogSelectedEquipment(null);
                  }}
                  variant="outlined"
                  sx={{ 
                    borderRadius: 2, 
                    px: 3, 
                    py: 1, 
                    fontSize: '0.9rem', 
                    fontWeight: 500,
                    textTransform: 'none',
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    '&:hover': {
                      border: `1px solid ${theme.palette.grey[600]}`,
                      transform: 'translateY(-1px)'
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    if (dialogSelectedEquipment) {
                      navigate(`/company/${companyId}/equipment-maintenance/${selectedDepartment.id}/${dialogSelectedEquipment.id}`);
                      setEquipmentDialogOpen(false);
                      setEquipmentSearchTerm('');
                      setDialogSelectedEquipment(null);
                    }
                  }}
                  variant="contained"
                  disabled={!dialogSelectedEquipment}
                  sx={{ 
                    borderRadius: 2, 
                    px: 3, 
                    py: 1, 
                    fontSize: '0.9rem', 
                    fontWeight: 500,
                    textTransform: 'none',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                      transform: 'translateY(-1px)',
                      boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`
                    },
                    '&:disabled': {
                      background: alpha(theme.palette.grey[400], 0.5),
                      color: alpha(theme.palette.grey[600], 0.7)
                    }
                  }}
                >
                  Confirm Selection
                </Button>
              </DialogActions>
            </Dialog>

            {/* Maintenance Log Form Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
              <DialogTitle>{editMode ? 'Edit Maintenance Log' : 'Add Maintenance Log'}</DialogTitle>
              <DialogContent>
                <Box sx={{ pt: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="S.No"
                        value={formData.serial_number}
                        disabled
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DateTimePicker
                        label="Log Date"
                        value={formData.log_date}
                        onChange={(newValue) => setFormData(prev => ({ ...prev, log_date: newValue }))}
                        ampm={timeFormat === '12hour'}
                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Breakdown / Stoppage Details"
                        value={formData.breakdown_details}
                        onChange={(e) => setFormData(prev => ({ ...prev, breakdown_details: e.target.value }))}
                        fullWidth
                        multiline
                        rows={2}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Nature of Breakdown"
                        value={formData.nature_of_breakdown}
                        onChange={(e) => setFormData(prev => ({ ...prev, nature_of_breakdown: e.target.value }))}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DateTimePicker
                        label="Stopped At"
                        value={formData.stopped_at}
                        onChange={(newValue) => setFormData(prev => ({ ...prev, stopped_at: newValue }))}
                        ampm={timeFormat === '12hour'}
                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DateTimePicker
                        label="Restarted At"
                        value={formData.restarted_at}
                        onChange={(newValue) => setFormData(prev => ({ ...prev, restarted_at: newValue }))}
                        ampm={timeFormat === '12hour'}
                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                      />
                    </Grid>
                    {formData.stopped_at && formData.restarted_at && (
                      <Grid item xs={12}>
                        <Alert severity="info">
                          Time Taken: {formatTimeTaken(calculateTimeTaken(formData.stopped_at, formData.restarted_at))}
                        </Alert>
                      </Grid>
                    )}
                  </Grid>

                  <Divider sx={{ my: 3 }} />
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>Spare Parts</Typography>
                  {formData.spare_parts.map((part, index) => (
                    <Paper key={index} sx={{ p: 2, mb: 2, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, borderRadius: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Spare Part Description"
                            value={part.spare_part_description}
                            onChange={(e) => updateSparePart(index, 'spare_part_description', e.target.value)}
                            fullWidth
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            label="Cost"
                            value={part.cost}
                            onChange={(e) => updateSparePart(index, 'cost', e.target.value)}
                            fullWidth
                            size="small"
                            type="number"
                            InputProps={{
                              inputProps: { min: 0 }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <Tooltip title={canDelete ? "Remove Spare Part" : "No permission to delete"}>
                            <span>
                              <IconButton 
                                onClick={() => removeSparePart(index)} 
                                disabled={!canDelete}
                                color="error"
                                sx={{ 
                                  border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                                  borderRadius: 2,
                                  '&.Mui-disabled': {
                                    color: alpha(theme.palette.error.main, 0.3),
                                    border: `1px solid ${alpha(theme.palette.grey[400], 0.2)}`,
                                    cursor: 'not-allowed'
                                  }
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Grid>
                        
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', mb: 1, display: 'block' }}>
                            Invoice Attachment (Optional)
                          </Typography>
                          
                          {part.invoice_file_name ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <PictureAsPdfIcon color="error" sx={{ fontSize: 20 }} />
                              <Typography variant="caption" sx={{ fontWeight: 500, flex: 1 }}>
                                {part.invoice_file_name}
                              </Typography>
                              <Tooltip title="View Invoice">
                                <IconButton 
                                  size="small"
                                  onClick={() => viewSparePartInvoice(part.invoice_file_path, part.invoice_file_name)}
                                  sx={{
                                    bgcolor: alpha(theme.palette.info.main, 0.1),
                                    '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.2) }
                                  }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={canDelete ? "Remove Invoice" : "No permission to delete"}>
                                <span>
                                  <IconButton 
                                    size="small"
                                    disabled={!canDelete}
                                    onClick={() => handleRemoveSparePartFile(index)}
                                    sx={{
                                      bgcolor: alpha(theme.palette.error.main, 0.1),
                                      '&:hover:not(:disabled)': { bgcolor: alpha(theme.palette.error.main, 0.2) },
                                      '&.Mui-disabled': {
                                        bgcolor: alpha(theme.palette.grey[400], 0.1),
                                        color: alpha(theme.palette.text.primary, 0.3),
                                        cursor: 'not-allowed'
                                      }
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          ) : (
                            <>
                              <input
                                accept=".pdf,.jpg,.jpeg,.png"
                                style={{ display: 'none' }}
                                id={`spare-part-invoice-${index}`}
                                type="file"
                                onChange={(e) => {
                                  if (e.target.files && e.target.files[0]) {
                                    handleSparePartFileUpload(index, e.target.files[0]);
                                  }
                                }}
                              />
                              <label htmlFor={`spare-part-invoice-${index}`}>
                                <Button
                                  variant="outlined"
                                  component="span"
                                  startIcon={<CloudUploadIcon />}
                                  size="small"
                                  sx={{ 
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                                  }}
                                >
                                  Upload Invoice
                                </Button>
                              </label>
                            </>
                          )}
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                  <Button startIcon={<AddIcon />} onClick={addSparePart} variant="outlined" size="small">
                    Add Spare Part
                  </Button>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseDialog}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
                  {submitting ? 'Saving...' : editMode ? 'Update' : 'Add'}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
              <DialogTitle>Confirm Delete</DialogTitle>
              <DialogContent>
                <Typography>Are you sure you want to delete this maintenance log?</Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
                <Button onClick={handleConfirmDelete} variant="contained" color="error">
                  Delete
                </Button>
              </DialogActions>
            </Dialog>

            {/* Approval Dialog */}
            <Dialog 
              open={approvalDialogOpen} 
              onClose={() => setApprovalDialogOpen(false)}
              maxWidth="sm"
              fullWidth
              disableScrollLock={true}
              PaperProps={{
                sx: {
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`
                }
              }}
            >
              <DialogTitle sx={{ 
                fontWeight: 500,
                fontSize: '1.2rem',
                color: 'primary.main',
                borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: alpha(theme.palette.primary.main, 0.05),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 3
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CheckCircleIcon sx={{ color: 'success.main' }} />
                  <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main' }}>
                    Approve Maintenance Log
                  </Typography>
                </Box>
                <IconButton 
                  onClick={() => setApprovalDialogOpen(false)} 
                  size="small"
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    '&:hover': {
                      transform: 'scale(1.1)',
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.2)}`
                    }
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              
              <DialogContent sx={{ 
                backgroundColor: alpha(theme.palette.grey[50], 0.5), 
                p: 3,
                maxHeight: '70vh', 
                overflowY: 'auto',
                mt: 4
              }}>
                {approvalLog && (
                  <Grid container spacing={3}>
                    {/* Log Summary */}
                    <Grid item xs={12}>
                      <Card sx={{ 
                        p: 3, 
                        border: `1px solid ${theme.palette.grey[400]}`, 
                        borderRadius: 3, 
                        backgroundColor: 'white',
                        boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`
                      }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                          Maintenance Log Summary
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                              S.No
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {approvalLog.serial_number}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                              Date
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {formatDate(approvalLog.log_date)}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                              Breakdown Details
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {approvalLog.breakdown_details || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                              Nature of Breakdown
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {approvalLog.nature_of_breakdown || 'N/A'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Card>
                    </Grid>

                    {/* Signature Section */}
                    <Grid item xs={12}>
                      <Card sx={{ 
                        p: 3, 
                        border: `1px solid ${theme.palette.grey[400]}`, 
                        borderRadius: 3, 
                        backgroundColor: 'white',
                        boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`
                      }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                          Approval Signature
                        </Typography>
                        <Grid container spacing={3}>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Your Name (Electronic Signature) *"
                              value={signatureName}
                              onChange={(e) => setSignatureName(e.target.value)}
                              required
                              placeholder="Enter your full name to approve this log"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2
                                }
                              }}
                            />
                          </Grid>
                          
                          {signatureName && (
                            <Grid item xs={12}>
                              <Box sx={{ 
                                border: `1px solid ${theme.palette.primary.main}`, 
                                p: 3, 
                                borderRadius: 2, 
                                bgcolor: alpha(theme.palette.primary.main, 0.05)
                              }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                                  ELECTRONIC SIGNATURE PREVIEW:
                                </Typography>
                                <Box sx={{ 
                                  fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                                  fontSize: '24px', 
                                  color: theme.palette.text.primary,
                                  textShadow: '1px 1px 1px rgba(0,0,0,0.1)',
                                  mb: 1
                                }}>
                                  {signatureName}
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                  Signed: {dayjs().format('MMM DD, YYYY HH:mm')}
                                </Typography>
                              </Box>
                            </Grid>
                          )}

                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              multiline
                              rows={3}
                              label="Approval Notes (Optional)"
                              value={approvalNotes}
                              onChange={(e) => setApprovalNotes(e.target.value)}
                              placeholder="Add any notes or comments about this approval..."
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2
                                }
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Card>
                    </Grid>
                  </Grid>
                )}
              </DialogContent>
              
              <DialogActions sx={{ 
                p: 3, 
                gap: 2, 
                borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
              }}>
                <Button 
                  onClick={() => setApprovalDialogOpen(false)}
                  startIcon={<CloseIcon />}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1.5,
                    color: theme.palette.text.secondary,
                    fontWeight: 500,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.04)
                    }
                  }}
                >
                  Cancel
                </Button>
                
                <Button 
                  onClick={handleApprovalSubmit}
                  variant="contained"
                  startIcon={<CheckCircleIcon />}
                  disabled={!signatureName.trim()}
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                    color: 'white',
                    fontWeight: 500,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`,
                      boxShadow: `0 6px 20px ${alpha(theme.palette.success.main, 0.4)}`
                    },
                    '&:disabled': {
                      background: theme.palette.grey[400],
                      color: theme.palette.grey[100]
                    }
                  }}
                >
                  Approve Log
                </Button>
              </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
              open={snackbar.open}
              autoHideDuration={4000}
              onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
              <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
                {snackbar.message}
              </Alert>
            </Snackbar>
          </Container>
        </Box>
      </Fade>
    </LocalizationProvider>
  );
};

export default EquipmentMaintenance;